import { useCallback } from "react"
import { Settings as ReactSlickProps } from "react-slick"
import {
  BoxProps,
  Heading,
  HStack,
  Image,
  Progress,
  Stack,
  StyleProps,
  Text,
  VStack,
} from "@chakra-ui/react"
import { DepositForAccount } from "@mezo/api"

import stbtcToken from "#/assets/tokens/stbtc-token.svg"
import { selectDepositMaxMintAmount, useDappSelector } from "#/redux-state"
import TooltipLabel from "#/shared/components/TooltipLabel"
import { BITCOIN_TOKENS, FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import { DepositMigrationStatus } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  getDepositMigrationStatus,
} from "#/shared/utils"
import DepositCarousel from "#/ui-old/DepositCarousel"
import DepositCarouselItemContainer from "#/ui-old/DepositCarousel/DepositCarouselItemContainer"

type StbtcDepositsCarouselProps = BoxProps &
  Pick<ReactSlickProps, "centerPadding"> & {
    variant: "mint" | "manage"
    mainLabelText?: string
    deposits: DepositForAccount[]
    onItemChange?: (previousIndex: number, nextIndex: number) => void
  }

export default function StbtcDepositsCarousel({
  variant,
  mainLabelText,
  deposits,
  centerPadding,
  onItemChange,
  ...props
}: StbtcDepositsCarouselProps) {
  const depositMaxMintAmountFromStore = useDappSelector(
    selectDepositMaxMintAmount,
  )

  const renderDepositItem = useCallback(
    (
      deposit: DepositForAccount,
      isActive: boolean,
      mainItemContainerProps: StyleProps,
    ) => {
      const {
        amount,
        id,
        token: originalToken,
        mintedAmount,
        migration,
      } = deposit

      const bigIntDepositAmount = BigInt(amount)

      const bigIntMaxTotalMintAmountForThisDeposit =
        BigInt(mintedAmount) + depositMaxMintAmountFromStore

      const maxTotalMintAmountForThisDeposit =
        Number(
          bigIntToHumanReadableFormat(
            bigIntMaxTotalMintAmountForThisDeposit,
            18,
            FRIENDLY_DECIMALS_AMOUNT,
          ),
        ) ?? 0

      const userDepositAmount = bigIntToHumanReadableFormat(
        bigIntDepositAmount,
        18,
        FRIENDLY_DECIMALS_AMOUNT,
      )

      const parsedMintAmount = bigIntToHumanReadableFormat(
        BigInt(mintedAmount),
        18,
        FRIENDLY_DECIMALS_AMOUNT,
      )

      const fullParsedMintAmount = bigIntToHumanReadableFormat(
        BigInt(mintedAmount),
        18,
        18,
      )

      const isSmallAmount =
        Number(fullParsedMintAmount) < 10 ** -FRIENDLY_DECIMALS_AMOUNT &&
        fullParsedMintAmount !== "0"

      const displayedMintAmount =
        isSmallAmount && fullParsedMintAmount !== "0" ? "<1" : parsedMintAmount

      const mintingProgress =
        maxTotalMintAmountForThisDeposit === 0
          ? 0
          : Math.min(
              100,
              (Number(parsedMintAmount) /
                Number(maxTotalMintAmountForThisDeposit)) *
                100,
            )

      const migrationStatus = getDepositMigrationStatus(
        originalToken,
        migration,
      )

      const token =
        migrationStatus === DepositMigrationStatus.Completed
          ? "tBTC"
          : originalToken

      const depositTokenImage = BITCOIN_TOKENS.find(
        (bitcoinToken) => bitcoinToken.token === token,
      )?.icon

      return (
        <DepositCarouselItemContainer
          key={`carousel-deposit-${id}`}
          isActive={isActive}
          height={variant === "mint" ? "155px" : "auto"}
          mb={deposits.length > 1 ? "24px" : 0}
          {...mainItemContainerProps}
        >
          {variant === "manage" && (
            <Text variant="labelSmall" color="primary.white.70">
              Minted
            </Text>
          )}
          <Stack
            direction={variant === "mint" ? "column" : "row"}
            alignItems={variant === "mint" ? "start" : "center"}
            justifyContent="space-between"
            h="100%"
          >
            {variant === "mint" ? (
              <Heading variant="h3" lineHeight="40px" fontSize="32px">
                {userDepositAmount}&nbsp;{token}
              </Heading>
            ) : (
              <>
                {isSmallAmount ? (
                  <TooltipLabel value={fullParsedMintAmount} variant="light">
                    <Heading variant="h3">{displayedMintAmount} stBTC</Heading>
                  </TooltipLabel>
                ) : (
                  <Heading variant="h3">{displayedMintAmount} stBTC</Heading>
                )}

                <HStack>
                  <Image
                    src={depositTokenImage}
                    width="24px"
                    filter="invert(1)"
                  />
                  <Text variant="body">
                    {userDepositAmount}&nbsp;{token}
                  </Text>
                </HStack>
              </>
            )}

            {variant === "mint" && (
              <VStack w="100%" alignItems="start">
                <HStack gap="1" mb="4px">
                  <Text variant="label" fontWeight="400">
                    Minted:
                  </Text>
                  <Image src={stbtcToken} h="16px" w="16px" />
                  {isSmallAmount ? (
                    <TooltipLabel value={fullParsedMintAmount}>
                      <Text variant="label" fontWeight="400">
                        {displayedMintAmount} stBTC
                      </Text>
                    </TooltipLabel>
                  ) : (
                    <Text variant="label" fontWeight="400">
                      {displayedMintAmount} stBTC
                    </Text>
                  )}
                </HStack>
                <Progress
                  variant="smallerBackgroundBar"
                  w="100%"
                  value={mintingProgress}
                  size="xs"
                  colorScheme="stbtc.darkOrange"
                  bg="primary.white.30"
                  opacity={!isActive ? 0 : 1}
                />
              </VStack>
            )}
          </Stack>
        </DepositCarouselItemContainer>
      )
    },
    [depositMaxMintAmountFromStore, deposits.length, variant],
  )

  return (
    <DepositCarousel
      mainLabelText={mainLabelText}
      deposits={deposits}
      centerPadding={centerPadding}
      onItemChange={onItemChange}
      renderDepositItem={renderDepositItem}
      {...props}
    />
  )
}
